<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { load as loadRecaptcha } from 'recaptcha-v3';
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';

import useI18n from '@/composables/useI18n';
import { useAuthStore } from '@/stores/auth/auth';
import { RecoverSessionParams } from '@/stores/auth/types';

import authService from '../api/auth';
import ExpireSignIn from '../components/LockScreen/ExpireSignIn.vue';
import { useAuth } from '../composables/useAuth';
import config from '../config';
import LoginContainer from '../layouts/LoginContainer.vue';


const route = useRoute();
const authStore = useAuthStore();
const { translate } = useI18n();
const { generateProviderRedirectUrl, getProviderByName } = useAuth();

const username = ref<string | null>(null);
const accountId = ref<string | null>(null);
const userId = ref<string | null>(null);
const provider = ref<string | null>(null);
const userIdentityProvider = ref<string | null>(null);

const token = ref('');

const authenticating = ref(false);
const keepLoggedIn = ref(false);
const googleSSOLoading = ref(false);
const error = ref('');
const ssoLoginError = ref('');

const {
  error: authError,
  availableUsers,
  sso_needs_two_factor,
  redirectPath,
  twoFactorCheck,
} = storeToRefs(authStore);

const identityProvider = computed(() => getProviderByName(userIdentityProvider.value || ''));

const titleText = computed(() => {
  if (userIdentityProvider.value) {
    return {
      'Due to inactivity': 'Due to inactivity, your session has expired external',
      'Provider name': `${identityProvider.value?.buttonLabel || identityProvider.value?.name}`,
      'user or sign out': 'user or sign out to start a new session',
    };
  }
  return 'Due to inactivity, your session has expired normal';
});

const logoutUrl = computed(() => {
  const encodedRedirectPath = encodeURIComponent(`${window.location.origin}/#/`);
  return `${config.AUTH_UI_URL}/logout?redirectPath=${encodedRedirectPath}`;
});

const GOOGLE_LOGIN = computed(() => {
  return config.GOOGLE_LOGIN;
});

watch(authError, (newValue) => {
  if (newValue) {
    let message = (newValue as any)?.response?.data.message || newValue;
    if (newValue instanceof Error) {
      message = newValue.message;
    }
    switch (message) {
      case 'Access suspended':
        error.value = 'Account access is suspended. Please contact OneReach.ai support.';
        break;
      case 'Two Factor token invalid':
        error.value = 'Two factor code invalid or expired. Please try again.';
        break;
      case 'Invalid verification code':
        error.value = 'Verification code id invalid or expired. Please try again.';
        break;
      case 'Invalid username and/or password':
        error.value = translate('login.login.The email and password combination is not valid');
        break;
      case 'Wrong credentials':
        error.value = translate('login.login.The email and password combination is not valid');
        break;
      case 'You have exceeded the request limit':
        error.value = translate('multi_user.list_users.Too many requests');
        break;
      default:
        error.value = 'Network Error. Please check your connection and try again.';
        break;
    }
  }
});

watch(twoFactorCheck, (value) => {
  if (value) {
    authenticating.value = false;
    error.value = '';
  }
});


const {
  recoverSession,
  loadUsers,
  ssoLoginMFA,
  setSSOTwoFactor,
} = authStore;

const setAuthToken = (newToken: string) => {
  authenticating.value = false;
  token.value = newToken;
};

const logout = () => {
  window.parent.location.href = logoutUrl.value;
};

const loginWithProvider = () => {
  const provider = getProviderByName(userIdentityProvider.value || '');
  const authProviderUrl = generateProviderRedirectUrl(
    provider?.name || '',
    '',
    {
      isChild: true,
    },
    {
      username: username.value,
      accountId: accountId.value,
      userId: userId.value,
      isChild: true,
    },
  );

  window.open(
    authProviderUrl,
    'Login',
    `width=${screen.width / 2},height=${screen.height / 2},left=20,top=20,toolbar=0,status=0`);
};

const googleCode = ref('');

const submit = async ({ credentials, keepLoggedIn, rememberTwoFactor },
  // TODO: Split in two after adding "Forgot passsword feature"
  isOpenRecovery2fa: boolean,
  twoFacorCode: string) => {
  if (!token.value || !userId.value || !accountId.value) {
    error.value = 'No user information provided to restore session';
    return;
  }
  error.value = '';
  if (sso_needs_two_factor.value) {
    authenticating.value = true;
    await ssoLoginMFA(twoFacorCode).catch(e => console.warn(e));

    if (isOpenRecovery2fa && error.value) {
      authenticating.value = false;
      const newError = error.value;
      error.value = '';
      error.value = newError;
      return;
    }
    authenticating.value = true;
    return;
  }
  if ((credentials.username && credentials.password) || googleCode.value) {
    const recaptcha = await loadRecaptcha(config.GOOGLE_CAPTCHA_SITE_KEY_V3);
    const captchaToken = await recaptcha.execute('signin');
    authenticating.value = true;

    const opts: RecoverSessionParams = {
      ...!googleCode.value && { credentials },
      keepLoggedIn: keepLoggedIn,
      rememberTwoFactor: rememberTwoFactor,
      userId: userId.value,
      accountId: accountId.value,
      captchaToken,
      ...(googleCode.value ? { googleCode: googleCode.value } : {}),
    };
    if (twoFactorCheck.value && twoFacorCode) {
      opts.verificationCode = twoFacorCode;
    }
    const res = await recoverSession(opts);

    if (res?.twoFactorCheck) {
      twoFactorCheck.value = res.twoFactorCheck;
    }

    authenticating.value = false;
    if (res?.token) {
      window.parent.postMessage({
        type: 'AUTH_SESSION_EXPIRATION_SUCCESS',
        token: res.token,
      }, '*');
    }

  } else if (!credentials.password) {
    error.value = 'You need to specify password';
  }
};

const resetError = () => {
  error.value = '';
};

const listenIframe = async (event) => {
  const { userId, accountId } = route.query;
  switch (event.data.type) {
    case 'AUTH_SESSION_EXPIRATION_TOKEN':
      setAuthToken(event.data.token);
      break;
    case 'SSO_SUCCESS_LOGIN':
      ssoLoginError.value = '';
      // eslint-disable-next-line no-case-declarations
      let result;
      if (event.data.user.tokenType === 'multi-user') {
        googleSSOLoading.value = true;
        await authService.saveMultiUser(event.data.user, keepLoggedIn.value);
        try {
          result = await authService.multiUserLoginUser({
            accountId: accountId as string,
            id: userId as string,
            long: keepLoggedIn.value,
          });
        } catch (e) {
          ssoLoginError.value = (e as any).response.data.message || e;
          return;
        } finally {
          googleSSOLoading.value = false;
        }
      } else {
        authService.saveUser({
          user: event.data.user,
          long: keepLoggedIn.value,
        });
        result = event.data.user;
      }
      window.parent.postMessage({
        type: 'AUTH_SESSION_EXPIRATION_SUCCESS',
        token: result.token,
      }, '*');
      break;
    case 'SSO_TWO_FACTOR_CHECK':
      twoFactorCheck.value = true;
      error.value = event.data.error || '';
      setSSOTwoFactor(event.data);
      break;
  }
};

onMounted(() => {
  window.addEventListener('message', listenIframe);
  window.parent.postMessage({ type: 'AUTH_SESSION_EXPIRATION_MOUNTED' }, '*');
  if (!availableUsers.value.length) {
    loadUsers();
  }
  authenticating.value = true;
  username.value = (route.query.username as string).trim();
  userId.value = route.query.userId as string;
  accountId.value = route.query.accountId as string;
  provider.value = route.query.provider as string;
  userIdentityProvider.value = route.query.identityProvider as string;

  if (!username.value) {
    error.value = 'Username is required';
  }
});
</script>

<template>
  <LoginContainer
    ref="cookieExpireModal"
    class="relogin-modal"
  >
    <ExpireSignIn
      v-model:keep-logged-in="keepLoggedIn"
      v-model:google-code="googleCode"
      :username="username"
      :redirect-path="redirectPath"
      :two-factor-check="twoFactorCheck"
      :error="error"
      :title-text="titleText"
      :sso-error="ssoLoginError"
      :google-loading="googleSSOLoading"
      :google-login="GOOGLE_LOGIN"
      :loading="authenticating"
      :user-identity-provider="userIdentityProvider"
      @submit="submit"
      @logout="logout"
      @providerLogin="loginWithProvider"
      @dismissSso="ssoLoginError = ''"
      @resetError="resetError"
    />
  </LoginContainer>
</template>
